import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const emptyPiece = { weight: '', length_in: '', width_in: '', height_in: '' };

const emptyRow = {
  destination_zip: '',
  is_residential: false,
  inside_charges: false,
  unpack_debris: false,
  liftgate_mode: 'auto',
  two_man_mode: 'auto',
  special_applied: false,
  special_time: 'normal',
  pieces: []
};

// ── Piece Entry Modal ──────────────────────────────────────────
const PieceModal = ({ row, customer, onConfirm, onClose }) => {
  const [pieces, setPieces] = useState(
    row.pieces && row.pieces.length > 0 ? row.pieces : [{ ...emptyPiece, id: Date.now() }]
  );
  const [preview, setPreview] = useState(null);
  const previewTimer = React.useRef(null);

  const addPiece = () => setPieces(prev => [...prev, { ...emptyPiece, id: Date.now() }]);
  const removePiece = (id) => {
    if (pieces.length === 1) return toast.error('At least one piece is required');
    setPieces(prev => prev.filter(p => p.id !== id));
  };
  const updatePiece = (id, field, value) => {
    setPieces(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  useEffect(() => {
    clearTimeout(previewTimer.current);
    if (!row.destination_zip || row.destination_zip.length < 5) return;
    const validPieces = pieces.filter(p => p.weight);
    if (validPieces.length === 0) return;

    previewTimer.current = setTimeout(async () => {
      try {
        const res = await API.post('/invoices/preview', {
          customer_id: customer?.customer_id,
          destination_zip: row.destination_zip,
          is_residential: row.is_residential,
          inside_charges: row.inside_charges,
          unpack_debris: row.unpack_debris,
          liftgate_mode: row.liftgate_mode || 'auto',
          two_man_mode: row.two_man_mode || 'auto',
          special_applied: row.special_applied || false,
          special_time: row.special_time || 'normal',
          pieces: validPieces.map(p => ({
            weight: parseFloat(p.weight) || 0,
            length_in: parseFloat(p.length_in) || 0,
            width_in: parseFloat(p.width_in) || 0,
            height_in: parseFloat(p.height_in) || 0
          }))
        });
        setPreview(res.data);
      } catch {
        setPreview(null);
      }
    }, 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieces, row.destination_zip]);

  const totalActual = pieces.reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0);

  const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #E2E8F0', borderRadius: '4px', fontSize: '13px', outline: 'none' };
  const thStyle = { padding: '8px', background: '#1A2A3A', color: 'white', fontSize: '12px', fontWeight: '500', textAlign: 'left', borderRight: '1px solid #F5A800' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="fcc-modal-pop" style={{ background: 'white', borderRadius: '12px', width: '700px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Piece Entry</h2>
            <p style={{ fontSize: '13px', color: '#5B6B7F', marginTop: '2px' }}>ZIP: <strong>{row.destination_zip || '—'}</strong></p>
          </div>
          <button onClick={onClose} className="fcc-press" style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#5B6B7F' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '40px' }}>#</th>
                <th style={thStyle}>Weight (lbs)</th>
                <th style={thStyle}>Length (in)</th>
                <th style={thStyle}>Width (in)</th>
                <th style={thStyle}>Height (in)</th>
                <th style={{ ...thStyle, borderRight: 'none', width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {pieces.map((piece, idx) => (
                <tr key={piece.id || idx}>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #E2E8F0', color: '#5B6B7F', fontSize: '12px', fontWeight: '600' }}>{idx + 1}</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                    <input style={inputStyle} type="number" value={piece.weight} onChange={e => updatePiece(piece.id || idx, 'weight', e.target.value)} placeholder="0" autoFocus={idx === pieces.length - 1} />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                    <input style={inputStyle} type="number" value={piece.length_in} onChange={e => updatePiece(piece.id || idx, 'length_in', e.target.value)} placeholder="0" />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                    <input style={inputStyle} type="number" value={piece.width_in} onChange={e => updatePiece(piece.id || idx, 'width_in', e.target.value)} placeholder="0" />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                    <input style={inputStyle} type="number" value={piece.height_in} onChange={e => updatePiece(piece.id || idx, 'height_in', e.target.value)} placeholder="0" />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0', textAlign: 'center' }}>
                    <button onClick={() => removePiece(piece.id || idx)} className="fcc-press" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={addPiece} className="fcc-press" style={{ marginTop: '10px', background: 'none', border: '1px dashed #F5A800', color: '#F5A800', borderRadius: '6px', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', width: '100%' }}>
            + Add Piece
          </button>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', background: '#F4F7FB' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Pieces</p>
              <p style={{ fontWeight: '600', fontSize: '16px' }}>{pieces.length}</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Total Actual Wt</p>
              <p style={{ fontWeight: '600', fontSize: '16px' }}>{totalActual.toFixed(0)} lbs</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Billable Wt</p>
              <p style={{ fontWeight: '600', fontSize: '16px' }}>{preview ? `${preview.billable_weight} lbs` : '—'}</p>
            </div>
            <div style={{ background: '#E9F7EF', border: '1px solid #A7D9BC', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Est. Total</p>
              <p style={{ fontWeight: '700', fontSize: '18px', color: '#15803D' }}>{preview ? `$${preview.final_total}` : '...'}</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '9px 20px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>Cancel</button>
            <button
              onClick={() => onConfirm(pieces.map(p => ({
                weight: parseFloat(p.weight) || 0,
                length_in: parseFloat(p.length_in) || 0,
                width_in: parseFloat(p.width_in) || 0,
                height_in: parseFloat(p.height_in) || 0
              })))}
              className="btn btn-primary fcc-press"
              style={{ width: 'auto', padding: '9px 24px' }}
            >
              Confirm Pieces ({pieces.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Customer Reference Calculator ──────────────────────────────
const CustomerCalculator = () => {
  const { user } = useAuth();
  const customer = { customer_id: user?.customer_id, name: user?.customer_name };

  const [rows, setRows] = useState([{ ...emptyRow, id: Date.now() }]);
  const [previews, setPreviews] = useState({});
  const [modalRowId, setModalRowId] = useState(null);

  const addRow = () => setRows(prev => [...prev, { ...emptyRow, id: Date.now() }]);
  const removeRow = (id) => {
    setRows(prev => prev.length === 1 ? prev : prev.filter(r => r.id !== id));
    setPreviews(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const fetchPreviewForRow = async (row) => {
    if (!row.destination_zip || row.destination_zip.length < 5 || !row.pieces || row.pieces.length === 0) {
      setPreviews(prev => ({ ...prev, [row.id]: null }));
      return;
    }
    try {
      const res = await API.post('/invoices/preview', {
        customer_id: customer.customer_id,
        destination_zip: row.destination_zip,
        is_residential: row.is_residential,
        inside_charges: row.inside_charges,
        unpack_debris: row.unpack_debris,
        liftgate_mode: row.liftgate_mode || 'auto',
        two_man_mode: row.two_man_mode || 'auto',
        special_applied: row.special_applied || false,
        special_time: row.special_time || 'normal',
        pieces: row.pieces
      });
      setPreviews(prev => ({ ...prev, [row.id]: res.data }));
    } catch {
      setPreviews(prev => ({ ...prev, [row.id]: null }));
    }
  };

  const confirmPieces = (rowId, pieces) => {
    const row = rows.find(r => r.id === rowId);
    const updated = { ...row, pieces };
    setRows(prev => prev.map(r => r.id === rowId ? updated : r));
    setModalRowId(null);
    fetchPreviewForRow(updated);
  };

  const grandTotal = rows.reduce((sum, r) => {
    const p = previews[r.id];
    return sum + (p ? parseFloat(p.final_total) || 0 : 0);
  }, 0);

  // Invoice-style line items for a computed shipment
  const lineItems = (p) => [
    ['Base charge', p.base_charge],
    ['Fuel surcharge', p.fuel_surcharge],
    ['Liftgate', p.liftgate_fee],
    ['Residential', p.residential_charge],
    ['Two-man', p.two_man_charge],
    ['Inside delivery', p.inside_charge_amt],
    ['Unpack / debris', p.unpack_charge_amt],
    ['Special', p.special_charges]
  ];

  const inputStyle = { padding: '7px 9px', border: '1px solid #E2E8F0', borderRadius: '5px', fontSize: '13px', outline: 'none' };
  const thStyle = { padding: '10px 8px', background: '#1A2A3A', color: 'white', fontSize: '12px', fontWeight: '500', textAlign: 'left', borderRight: '1px solid #F5A800', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '8px', borderBottom: '1px solid #E2E8F0', borderRight: '1px solid #f0f0f0', fontSize: '13px', verticalAlign: 'top' };
  const modeSelectStyle = { fontSize: '11px', padding: '3px 5px', border: '1px solid #E8B84B', borderRadius: '4px', background: '#FDF4DD', outline: 'none', cursor: 'pointer' };

  const modalRow = rows.find(r => r.id === modalRowId);
  const COLSPAN = 12;

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Delivery Calculator</h1>
        <p style={{ color: '#5B6B7F', fontSize: '14px' }}>
          Estimate what a delivery should cost{customer.name ? ` for ${customer.name}` : ''}. This is a reference tool — nothing is saved.
        </p>
      </div>

      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Destination ZIP</th>
                <th style={thStyle}>Pieces</th>
                <th style={thStyle}>Residential</th>
                <th style={thStyle}>Inside</th>
                <th style={thStyle}>Unpack</th>
                <th style={thStyle}>Special</th>
                <th style={thStyle}>Zone</th>
                <th style={thStyle}>Bill Wt</th>
                <th style={thStyle}>Liftgate</th>
                <th style={thStyle}>2-Man</th>
                <th style={thStyle}>Est. Total</th>
                <th style={{ ...thStyle, borderRight: 'none' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const p = previews[row.id];
                const pieceCount = row.pieces ? row.pieces.length : 0;
                return (
                  <React.Fragment key={row.id}>
                    <tr>
                      <td style={tdStyle}>
                        <input
                          style={{ ...inputStyle, width: '110px' }}
                          value={row.destination_zip}
                          onChange={e => updateRow(row.id, 'destination_zip', e.target.value)}
                          onBlur={() => fetchPreviewForRow(row)}
                          placeholder="ZIP"
                          maxLength={5}
                        />
                      </td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => setModalRowId(row.id)}
                          className="fcc-press"
                          style={{ background: pieceCount ? '#E9F7EF' : 'none', border: `1px solid ${pieceCount ? '#A7D9BC' : '#E2E8F0'}`, borderRadius: '5px', padding: '7px 12px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}
                        >
                          {pieceCount ? `${pieceCount} piece${pieceCount > 1 ? 's' : ''}` : '+ Add pieces'}
                        </button>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input type="checkbox" checked={row.is_residential} onChange={e => { updateRow(row.id, 'is_residential', e.target.checked); fetchPreviewForRow({ ...row, is_residential: e.target.checked }); }} />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input type="checkbox" checked={row.inside_charges} onChange={e => { updateRow(row.id, 'inside_charges', e.target.checked); fetchPreviewForRow({ ...row, inside_charges: e.target.checked }); }} />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input type="checkbox" checked={row.unpack_debris} onChange={e => { updateRow(row.id, 'unpack_debris', e.target.checked); fetchPreviewForRow({ ...row, unpack_debris: e.target.checked }); }} />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={!!row.special_applied} onChange={e => { updateRow(row.id, 'special_applied', e.target.checked); fetchPreviewForRow({ ...row, special_applied: e.target.checked }); }} />
                            Special
                          </label>
                          {row.special_applied && p && ['A', 'B', 'C', 'D'].includes(p.zone) && (
                            <select style={{ ...inputStyle, width: '120px' }} value={row.special_time || 'normal'} onChange={e => { updateRow(row.id, 'special_time', e.target.value); fetchPreviewForRow({ ...row, special_time: e.target.value }); }}>
                              <option value="normal">Normal Hours</option>
                              <option value="after_hours">After Hours</option>
                              <option value="weekend">Weekend/Holiday</option>
                            </select>
                          )}
                          {row.special_applied && p && !['A', 'B', 'C', 'D'].includes(p.zone) && (
                            <span style={{ fontSize: '11px', color: '#5B6B7F', whiteSpace: 'nowrap' }}>{p.approx_miles} mi × rate</span>
                          )}
                        </div>
                      </td>
                      <td style={tdStyle}>{p ? p.zone : '—'}</td>
                      <td style={tdStyle}>{p ? `${p.billable_weight}` : '—'}</td>
                      <td style={tdStyle}>
                        {p ? (
                          <select
                            value={row.liftgate_mode || 'auto'}
                            onChange={e => { updateRow(row.id, 'liftgate_mode', e.target.value); fetchPreviewForRow({ ...row, liftgate_mode: e.target.value }); }}
                            style={modeSelectStyle}
                          >
                            <option value="auto">Auto {p.liftgate_auto ? '(on)' : '(off)'}</option>
                            <option value="waived">Waived</option>
                            <option value="applied">Applied</option>
                          </select>
                        ) : '—'}
                      </td>
                      <td style={tdStyle}>
                        {p ? (
                          <select
                            value={row.two_man_mode || 'auto'}
                            onChange={e => { updateRow(row.id, 'two_man_mode', e.target.value); fetchPreviewForRow({ ...row, two_man_mode: e.target.value }); }}
                            style={modeSelectStyle}
                          >
                            <option value="auto">Auto {p.two_man_auto ? '(on)' : '(off)'}</option>
                            <option value="waived">Waived</option>
                            <option value="applied">Applied</option>
                          </select>
                        ) : '—'}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: '700', color: '#15803D' }}>{p ? `$${p.final_total}` : '—'}</td>
                      <td style={{ ...tdStyle, borderRight: 'none', textAlign: 'center' }}>
                        <button onClick={() => removeRow(row.id)} className="fcc-press" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                      </td>
                    </tr>

                    {/* Invoice-style line-item breakdown */}
                    {p && (
                      <tr>
                        <td colSpan={COLSPAN} style={{ borderBottom: '2px solid #E2E8F0', background: '#FAFBFC', padding: '10px 16px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '18px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '600', color: '#5B6B7F', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Line items</span>
                            {lineItems(p).map(([label, val]) => {
                              const amt = parseFloat(val) || 0;
                              return (
                                <span key={label} style={{ display: 'inline-flex', flexDirection: 'column' }}>
                                  <span style={{ fontSize: '11px', color: '#5B6B7F' }}>{label}</span>
                                  <span style={{ fontSize: '13px', fontWeight: '600', color: amt > 0 ? '#0F1B2D' : '#B4BDC7' }}>${amt.toFixed(2)}</span>
                                </span>
                              );
                            })}
                            <span style={{ marginLeft: 'auto', display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                              <span style={{ fontSize: '11px', color: '#5B6B7F' }}>Total</span>
                              <span style={{ fontSize: '15px', fontWeight: '700', color: '#15803D' }}>${parseFloat(p.final_total).toFixed(2)}</span>
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #E2E8F0', background: '#F4F7FB' }}>
          <button onClick={addRow} className="fcc-press" style={{ background: 'none', border: '1px dashed #F5A800', color: '#B37A00', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px' }}>
            + Add Shipment
          </button>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '12px', color: '#5B6B7F', marginRight: '10px' }}>Estimated total</span>
            <span style={{ fontSize: '22px', fontWeight: '700', color: '#15803D' }}>${grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {modalRow && (
        <PieceModal
          row={modalRow}
          customer={customer}
          onConfirm={(pieces) => confirmPieces(modalRow.id, pieces)}
          onClose={() => setModalRowId(null)}
        />
      )}
    </div>
  );
};

export default CustomerCalculator;
